import { useState } from 'react';
import { chartDailySeries } from './domain';
import {
  VBX,
  VBY,
  bandSum,
  useChartGeometry,
  ChartStatusLegend,
  ChartBandsTooltip,
  ChartHoverSlices,
  ChartAxes,
  ChartFrame,
  BAR_SLOT_INSET,
  ChartNote,
} from './chartParts';
import type { ChartProps } from './BookingsLineChart';

// Breakdown by band — stacked bars per day, one segment per status or area
// (group-by). AC3 "breakdown by status". Carried from _prototype/bookings.jsx
// BookingsBarChart.
export function BookingsBarChart({ bookings, viewWindow, bands, title, onSwipe }: ChartProps) {
  const series = chartDailySeries(bookings, viewWindow.from, viewWindow.to);
  const [hoverIdx, setHoverIdx] = useState<number | null>(null);
  // Paging is what makes `series` shorter — the last page of a range is partial
  // — so an index taken in a fourteen-day window can outlive it. Every read
  // below asserted non-null on `series[hover]`, which is a promise the data
  // cannot keep. Deriving the point instead of indexing blind means a stale
  // index reads as "nothing hovered", which is the truth of it.
  const hover = hoverIdx != null && hoverIdx < series.length ? hoverIdx : null;

  // Empty renders INSIDE the frame, so the card keeps its height. A dashed
  // panel here made the page jump every time a range came back with no stays.
  if (series.length === 0) {
    return (
      <ChartFrame title={title} legend={<ChartStatusLegend bands={bands} />}>
        <ChartNote>No data in this range.</ChartNote>
      </ChartFrame>
    );
  }

  const { yMax, yTicks, xTicks } = useChartGeometry(series, bands);
  const yAt = (v: number) => VBY - (v / yMax) * VBY;
  const slotW = VBX / series.length;
  const inset = slotW * BAR_SLOT_INSET;
  const slotCenter = (i: number) => i * slotW + slotW / 2;
  const leftPct = hover != null ? (slotCenter(hover) / VBX) * 100 : 0;
  // Anchor the tooltip to the top of the hovered day's stack (sum of the active
  // bands), as a % of height.
  const anchorPct =
    hover != null
      ? Math.max(0, Math.min(100, (yAt(Math.min(bandSum(series[hover]!, bands), yMax)) / VBY) * 100))
      : 0;

  return (
    <ChartFrame title={title} legend={<ChartStatusLegend bands={bands} />}>
      <div className="chart-svg-wrap" onMouseLeave={() => setHoverIdx(null)}>
          <svg
            className="chart-svg"
            width="100%"
            height="100%"
            viewBox={`0 0 ${VBX} ${VBY}`}
            preserveAspectRatio="none"
            role="img"
            aria-label="Daily breakdown of dogs by booking status"
          >
            {yTicks.map((v, i) => (
              <line
                key={'g' + i}
                x1="0"
                y1={yAt(v)}
                x2={VBX}
                y2={yAt(v)}
                stroke="var(--border)"
                strokeWidth="1"
                vectorEffect="non-scaling-stroke"
              />
            ))}
            {series.map((p, i) => {
              const xL = i * slotW + inset;
              const w = Math.max(0.4, slotW - inset * 2);
              const dim = hover != null && hover !== i ? 0.55 : 1;
              let runningTotal = 0;
              const segments = bands.map((s) => {
                const v = p[s.key];
                if (v <= 0) return null;
                const yTop = yAt(runningTotal + v);
                const yBot = yAt(runningTotal);
                runningTotal += v;
                return (
                  <rect
                    key={s.key}
                    x={xL}
                    y={yTop}
                    width={w}
                    height={Math.max(0, yBot - yTop)}
                    fill={s.fill}
                    style={{ opacity: dim }}
                  />
                );
              });
              return <g key={i}>{segments}</g>;
            })}
            <ChartHoverSlices
              series={series}
              onIndex={setHoverIdx}
              onClear={() => setHoverIdx(null)}
              onSwipe={onSwipe}
            />
          </svg>
          <ChartBandsTooltip
            point={hover != null ? series[hover]! : null}
            bands={bands}
            leftPct={leftPct}
            anchorPct={anchorPct}
          />
        </div>
      <ChartAxes yTicks={yTicks} xTicks={xTicks} seriesLength={series.length} />
    </ChartFrame>
  );
}
